import { useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  Card,
  Checklist,
  EVENT_STATES,
  Field,
  Metric,
  Notice,
  Status,
  formatInZone,
  presentation,
} from '@gatecheckpro/design-system';
import type { OrganizerEvent } from '@gatecheckpro/contracts';
import { useCommand } from '../../lib/useCommand.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

const ATTENDEE_BASE_URL = import.meta.env.VITE_ATTENDEE_BASE_URL ?? 'http://localhost:5173';

/**
 * The overview and the publish decision.
 *
 * The checklist here is rendered from the server's own readiness evaluation, not
 * from a client-side re-derivation. An organizer who sees "ready" and is then
 * refused has been told two different things by one product.
 */
export function EventOverviewPage() {
  const { event, organizationId, reload } = useEventWorkspace();
  const publish = useCommand<OrganizerEvent>();
  const unpublish = useCommand<OrganizerEvent>();
  const [takedownReason, setTakedownReason] = useState('');
  const [confirmingTakedown, setConfirmingTakedown] = useState(false);

  const base = `/api/v1/organizer/organizations/${organizationId}/events/${event.eventId}`;
  const state = presentation(EVENT_STATES, event.status);
  const publicUrl = `${ATTENDEE_BASE_URL}/e/${event.slug}`;

  const totalCapacity = event.inventoryPools.reduce((sum, pool) => sum + pool.capacity, 0);
  const totalSaleable = event.ticketProducts
    .filter((product) => product.status === 'PUBLISHED')
    .reduce((sum, product) => sum + product.capacity.allocated, 0);
  const nextSession = event.sessions
    .filter((session) => session.status === 'SCHEDULED')
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-grid">
        <Metric
          label="Sessions"
          value={String(event.sessions.filter((s) => s.status === 'SCHEDULED').length)}
          hint={
            nextSession === undefined
              ? 'None scheduled'
              : `Next ${formatInZone(nextSession.startsAt, event.timezone)}`
          }
        />
        <Metric
          label="Gates configured"
          value={String(event.gatePolicies.length)}
          hint={event.venue?.name ?? 'No venue chosen'}
        />
        <Metric
          label="Ticket products"
          value={String(
            event.ticketProducts.filter((product) => product.status === 'PUBLISHED').length,
          )}
          hint={`${String(event.ticketProducts.length)} in total`}
        />
        <Metric
          label="Can ultimately sell"
          value={totalSaleable.toLocaleString()}
          hint={`of ${totalCapacity.toLocaleString()} configured capacity`}
        />
      </div>

      <Card
        title={event.status === 'PUBLISHED' ? 'This event is public' : 'Publishing'}
        eyebrow="Status"
      >
        <div className="gc-stack">
          <div className="gc-row">
            <Status
              tone={state.tone}
              glyph={state.glyph}
              label={state.label}
              title={state.meaning}
            />
            {event.publishedAt !== null && (
              <span className="gc-muted">
                {event.status === 'PUBLISHED' ? 'Public since ' : 'Was public from '}
                {formatInZone(event.publishedAt, event.timezone, { withYear: true })}
              </span>
            )}
          </div>

          <FailureNotice error={publish.error ?? unpublish.error} />

          {event.status === 'PUBLISHED' ? (
            <>
              <div className="gc-stack gc-stack--tight">
                <span className="gc-field__label">Public link</span>
                <a className="gc-mono" href={publicUrl} target="_blank" rel="noreferrer">
                  {publicUrl}
                </a>
                <span className="gc-muted">
                  This link cannot change now that the event is public — posters and messages
                  already carry it.
                </span>
              </div>

              {confirmingTakedown ? (
                <div className="gc-stack">
                  <Notice tone="caution" title="Taking this event down">
                    The public page will immediately say nothing exists there. Anyone holding the
                    link sees the same thing as someone who invented it.
                  </Notice>
                  <Field
                    label="Reason"
                    required
                    placeholder="LINEUP_CHANGED"
                    hint="Recorded permanently against the event."
                    value={takedownReason}
                    onChange={(input) => setTakedownReason(input.target.value)}
                  />
                  <div className="gc-row">
                    <Button
                      variant="destructive"
                      busy={unpublish.busy}
                      disabled={takedownReason.trim().length < 2}
                      onClick={() =>
                        void unpublish
                          .run(`${base}/unpublish`, {
                            method: 'POST',
                            body: { reasonCode: takedownReason.trim().toUpperCase() },
                          })
                          .then((result) => {
                            if (result !== null) {
                              setConfirmingTakedown(false);
                              reload();
                            }
                          })
                      }
                    >
                      Take it down
                    </Button>
                    <Button onClick={() => setConfirmingTakedown(false)}>Keep it public</Button>
                  </div>
                </div>
              ) : (
                <div className="gc-row">
                  <Button variant="destructive" onClick={() => setConfirmingTakedown(true)}>
                    Take down
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <Checklist
                doneLabel="Before you can publish"
                outstanding={event.publishReadiness.blockers.map((blocker) => ({
                  code: `${blocker.code}-${blocker.message}`,
                  message: blocker.message,
                  action: (
                    <Link to={sectionPath(blocker.section)} className="gc-button gc-button--quiet">
                      Fix in {sectionLabel(blocker.section)} →
                    </Link>
                  ),
                }))}
                readyTitle="Ready to publish"
                readyBody="Everything an attendee needs is configured. Publishing makes the event page live immediately."
              />

              <div className="gc-row">
                <Button
                  variant="primary"
                  size="large"
                  busy={publish.busy}
                  disabled={!event.publishReadiness.ready}
                  onClick={() =>
                    void publish
                      .run(`${base}/publish`, { method: 'POST', body: {} })
                      .then((result) => {
                        if (result !== null) reload();
                      })
                  }
                >
                  Publish this event
                </Button>
                {!event.publishReadiness.ready && (
                  <span className="gc-muted">
                    {event.publishReadiness.blockers.length} thing
                    {event.publishReadiness.blockers.length === 1 ? '' : 's'} still to finish.
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      <Card title="Public details" eyebrow="What an attendee reads">
        <div className="gc-stack gc-stack--tight">
          <p>{event.publicSummary ?? <span className="gc-muted">No summary yet.</span>}</p>
          {event.publicDescription !== null && (
            <p className="gc-muted">{event.publicDescription}</p>
          )}
          <Link to="details">Edit public details</Link>
        </div>
      </Card>

      {event.internalNotes !== null && (
        <Card title="Internal notes" eyebrow="Never shown publicly">
          <p className="gc-muted">{event.internalNotes}</p>
        </Card>
      )}
    </div>
  );
}

function sectionPath(section: string): string {
  switch (section) {
    case 'SESSIONS':
      return 'sessions';
    case 'VENUE':
      return 'venue';
    case 'GATES':
      return 'gates';
    case 'TICKETS':
      return 'tickets';
    default:
      return 'details';
  }
}

function sectionLabel(section: string): string {
  switch (section) {
    case 'SESSIONS':
      return 'Sessions';
    case 'VENUE':
      return 'Venue';
    case 'GATES':
      return 'Gates & zones';
    case 'TICKETS':
      return 'Tickets & capacity';
    default:
      return 'Details';
  }
}
